import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Calculator, 
  ArrowRight, 
  Grid3X3,
  Package,
  Ruler,
  Info
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";

const TileCalculatorPage = () => {
  const [areaType, setAreaType] = useState("floor");
  const [wallArea, setWallArea] = useState(0);
  const [floorArea, setFloorArea] = useState(20);
  const [tileSize, setTileSize] = useState("600x600");
  const [customTileLength, setCustomTileLength] = useState(600);
  const [customTileWidth, setCustomTileWidth] = useState(600);
  const [wasteAllowance, setWasteAllowance] = useState(10);
  const [tilesPerBox, setTilesPerBox] = useState(4);
  const [pricePerBox, setPricePerBox] = useState(35);
  const [results, setResults] = useState(null);

  const tileSizes = [
    { value: "200x200", label: "200 x 200 mm", area: 0.04 },
    { value: "300x300", label: "300 x 300 mm", area: 0.09 },
    { value: "300x600", label: "300 x 600 mm", area: 0.18 },
    { value: "600x600", label: "600 x 600 mm", area: 0.36 },
    { value: "600x1200", label: "600 x 1200 mm", area: 0.72 },
    { value: "800x800", label: "800 x 800 mm", area: 0.64 },
    { value: "900x900", label: "900 x 900 mm", area: 0.81 },
    { value: "custom", label: "Custom Size", area: 0 }
  ];

  const wasteOptions = [
    { value: 10, label: "Standard (10%)", desc: "Straight layout" },
    { value: 15, label: "Complex (15%)", desc: "Diagonal or brick bond" },
    { value: 20, label: "Herringbone (20%)", desc: "Herringbone pattern" }
  ];

  useEffect(() => {
    calculateTiles();
  }, [areaType, wallArea, floorArea, tileSize, customTileLength, customTileWidth, wasteAllowance, tilesPerBox, pricePerBox]);

  const calculateTiles = () => {
    // Calculate total area
    let totalArea = 0;
    if (areaType === "wall") {
      totalArea = wallArea;
    } else if (areaType === "floor") {
      totalArea = floorArea;
    } else {
      totalArea = wallArea + floorArea;
    }

    if (totalArea <= 0) {
      setResults(null);
      return;
    }

    // Calculate tile area
    let tileArea;
    if (tileSize === "custom") {
      tileArea = (customTileLength / 1000) * (customTileWidth / 1000);
    } else {
      const selectedSize = tileSizes.find(s => s.value === tileSize);
      tileArea = selectedSize ? selectedSize.area : 0.36;
    }

    if (tileArea <= 0) {
      setResults(null);
      return;
    }

    // Calculate tiles needed
    const tilesNeeded = Math.ceil(totalArea / tileArea);
    const tilesWithWaste = Math.ceil(tilesNeeded * (1 + wasteAllowance / 100));
    const boxesNeeded = Math.ceil(tilesWithWaste / tilesPerBox);
    const totalCost = boxesNeeded * pricePerBox;

    setResults({
      totalArea: totalArea.toFixed(1),
      tilesNeeded,
      tilesWithWaste,
      boxesNeeded,
      totalCost
    });
  };

  return (
    <div data-testid="tile-calculator-page">
      {/* Hero */}
      <section className="py-16 bg-gray-50 grid-pattern">
        <div className="container-custom text-center">
          <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full mb-6">
            <Calculator className="w-4 h-4" />
            <span className="text-sm font-medium">Free Tool</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-montserrat">
            Tile Quantity Calculator
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Calculate exactly how many tiles and boxes you need for your project. 
            Includes waste allowance for different layouts.
          </p>
        </div>
      </section>

      {/* Calculator */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Input Section */}
              <div className="space-y-6">
                {/* Area Type */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Grid3X3 className="w-5 h-5 text-primary" />
                    Area Type
                  </h3>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { value: "wall", label: "Wall Tiling" },
                      { value: "floor", label: "Floor Tiling" },
                      { value: "both", label: "Both" }
                    ].map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setAreaType(option.value)}
                        className={`p-3 rounded-lg border-2 transition-all text-sm font-medium ${
                          areaType === option.value
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-gray-200 hover:border-primary/50"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Area Input */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Ruler className="w-5 h-5 text-primary" />
                    Tiling Area
                  </h3>
                  
                  {(areaType === "wall" || areaType === "both") && (
                    <div className="mb-4">
                      <div className="flex justify-between mb-2">
                        <Label>Wall Area (m²)</Label>
                        <span className="font-bold text-primary">{wallArea} m²</span>
                      </div>
                      <Slider
                        value={[wallArea]}
                        onValueChange={(v) => setWallArea(v[0])}
                        min={0}
                        max={100}
                        step={0.5}
                      />
                    </div>
                  )}
                  
                  {(areaType === "floor" || areaType === "both") && (
                    <div>
                      <div className="flex justify-between mb-2">
                        <Label>Floor Area (m²)</Label>
                        <span className="font-bold text-primary">{floorArea} m²</span>
                      </div>
                      <Slider
                        value={[floorArea]}
                        onValueChange={(v) => setFloorArea(v[0])}
                        min={0}
                        max={100}
                        step={0.5}
                      />
                    </div>
                  )}
                </div>

                {/* Tile Size */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h3 className="font-semibold text-gray-900 mb-4">Tile Size</h3>
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    {tileSizes.map((size) => (
                      <button
                        key={size.value}
                        onClick={() => setTileSize(size.value)}
                        className={`p-2 rounded-lg border-2 transition-all text-xs font-medium ${
                          tileSize === size.value
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-gray-200 hover:border-primary/50"
                        }`}
                      >
                        {size.label}
                      </button>
                    ))}
                  </div>
                  
                  {tileSize === "custom" && (
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div>
                        <Label className="text-sm">Length (mm)</Label>
                        <Input
                          type="number"
                          value={customTileLength}
                          onChange={(e) => setCustomTileLength(Number(e.target.value))}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Width (mm)</Label>
                        <Input
                          type="number"
                          value={customTileWidth}
                          onChange={(e) => setCustomTileWidth(Number(e.target.value))}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Waste Allowance */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h3 className="font-semibold text-gray-900 mb-4">Waste Allowance</h3>
                  <div className="space-y-2">
                    {wasteOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setWasteAllowance(option.value)}
                        className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                          wasteAllowance === option.value
                            ? "border-primary bg-primary/10"
                            : "border-gray-200 hover:border-primary/50"
                        }`}
                      >
                        <span className="font-medium text-gray-900">{option.label}</span>
                        <span className="text-sm text-gray-500 ml-2">{option.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Box Details */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Package className="w-5 h-5 text-primary" />
                    Box Details
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm">Tiles per Box</Label>
                      <Input
                        type="number"
                        value={tilesPerBox}
                        onChange={(e) => setTilesPerBox(Number(e.target.value))}
                        min={1}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Price per Box (£)</Label>
                      <Input
                        type="number"
                        value={pricePerBox}
                        onChange={(e) => setPricePerBox(Number(e.target.value))}
                        min={0}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Results Section */}
              <div>
                <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 sticky top-24">
                  <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">
                    Tile Calculation Results
                  </h3>

                  {results ? (
                    <>
                      <div className="space-y-4 mb-8">
                        <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                          <span className="text-gray-600">Total Tiling Area</span>
                          <span className="text-xl font-bold text-gray-900">{results.totalArea} m²</span>
                        </div>
                        
                        <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                          <span className="text-gray-600">Tiles Required</span>
                          <span className="text-xl font-bold text-gray-900">{results.tilesNeeded} tiles</span>
                        </div>
                        
                        <div className="flex justify-between items-center p-4 bg-blue-50 rounded-lg">
                          <span className="text-blue-700">With {wasteAllowance}% Waste</span>
                          <span className="text-xl font-bold text-blue-700">{results.tilesWithWaste} tiles</span>
                        </div>
                        
                        <div className="flex justify-between items-center p-4 bg-primary/10 rounded-lg">
                          <span className="text-primary font-medium">Boxes Needed</span>
                          <span className="text-2xl font-bold text-primary">{results.boxesNeeded} boxes</span>
                        </div>
                      </div>

                      <div className="bg-gradient-to-br from-primary to-blue-700 rounded-xl p-6 text-center text-white mb-6">
                        <p className="text-white/80 text-sm mb-1">Estimated Tile Cost</p>
                        <p className="text-4xl font-bold">£{results.totalCost.toLocaleString()}</p>
                        <p className="text-white/70 text-xs mt-1">Based on £{pricePerBox} per box</p>
                      </div>

                      <div className="bg-amber-50 rounded-lg p-4 mb-6">
                        <div className="flex items-start gap-2">
                          <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-amber-800">
                            This is an estimate for tiles only. Labour, adhesive, grout, and other materials are not included.
                          </p>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <Link to="/estimate" className="block">
                          <Button className="w-full bg-primary hover:bg-primary/90 py-5">
                            Get Full Project Estimate
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        </Link>
                        <Link to="/contact" className="block">
                          <Button variant="outline" className="w-full py-5">
                            Book a Quote Appointment
                          </Button>
                        </Link>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      <Calculator className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <p>Enter your tiling area to see results</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TileCalculatorPage;
